import { useCallback } from "react";

import { useFireAnalyticsEvents } from "@/components/fastshift/analytics";
import {
    ActionKey,
    ActionSubjectKey,
    ActionSubjectIdKey
} from "@/components/fastshift/analytics/constants";

import { useRelationshipsAnalyticsData } from "./use-relationships-analytics-data";

export const useRelationshipsAnalytics = () => {
    const { sourceKey, identityAttrs } = useRelationshipsAnalyticsData();
    const { fireUIEvent, fireTrackEvent } =
        useFireAnalyticsEvents(identityAttrs);

    // UI event callbacks
    const sendRequestAssistanceButtonClickUIEvent = useCallback(() => {
        if (!identityAttrs || !sourceKey) return;
        fireUIEvent({
            action: ActionKey.CLICKED,
            actionSubject: ActionSubjectKey.BUTTON,
            actionSubjectId: ActionSubjectIdKey.REQUEST_ASSISTANCE_BUTTON,
            source: sourceKey
        });
    }, [fireUIEvent, identityAttrs, sourceKey]);

    const sendRequestAssistanceModalCommentSubmitButtonClickUIEvent =
        useCallback(
            (supportTicketId: string) => {
                if (!identityAttrs || !sourceKey) return;
                fireUIEvent({
                    action: ActionKey.CLICKED,
                    actionSubject: ActionSubjectKey.BUTTON,
                    actionSubjectId:
                        ActionSubjectIdKey.REQUEST_FASTSHIFT_ASSISTANCE_MODAL_SUBMIT_BUTTON_CLICK,
                    source: sourceKey,
                    attributes: {
                        supportTicketId
                    }
                });
            },
            [fireUIEvent, identityAttrs, sourceKey]
        );

    // Manual track event triggers
    const sendRequestAssistanceModalDisplayedTrackEvent = useCallback(
        (isEmpty: boolean, supportTicketCount: number) => {
            if (!identityAttrs || !sourceKey) return;
            fireTrackEvent({
                action: ActionKey.DISPLAYED,
                actionSubject: ActionSubjectKey.MODAL,
                actionSubjectId:
                    ActionSubjectIdKey.REQUEST_FASTSHIFT_ASSISTANCE_MODAL,
                source: sourceKey,
                attributes: {
                    isEmpty,
                    supportTicketCount
                }
            });
        },
        [fireTrackEvent, identityAttrs, sourceKey]
    );

    const sendPostCommentOutcomeFlagDisplayedTrackEvent = useCallback(
        (successFlag: boolean, supportTicketId: string) => {
            if (!identityAttrs || !sourceKey) return;
            fireTrackEvent({
                action: ActionKey.DISPLAYED,
                actionSubject: ActionSubjectKey.FLAG,
                actionSubjectId:
                    ActionSubjectIdKey.REQUEST_FASTSHIFT_ASSISTANCE_COMMENT_CONFIRMATION_FLAG,
                source: sourceKey,
                attributes: {
                    successFlag,
                    supportTicketId
                }
            });
        },
        [fireTrackEvent, identityAttrs, sourceKey]
    );

    return {
        // UI Events (button clicks)
        sendRequestAssistanceButtonClickUIEvent,
        sendRequestAssistanceModalCommentSubmitButtonClickUIEvent,

        // Track Events (manual triggers)
        sendRequestAssistanceModalDisplayedTrackEvent,
        sendPostCommentOutcomeFlagDisplayedTrackEvent
    };
};
